import React, { useState, useEffect } from 'react';
import { 
  Package, Plus, X, Search, Edit2, 
  Archive, ChevronDown, ChevronRight, CheckCircle, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown 
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import API, { socket } from '../../services/api';

const ListeMp = () => {
  // --- États ---
  const [matieresPremieres, setMatieresPremieres] = useState([]);
  const [unites, setUnites] = useState([]); 
  const [categories, setCategories] = useState([]); 
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('catalogue');
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  // 🌟 États pour le tri des colonnes
  const [sortConfig, setSortConfig] = useState({ key: 'nom', direction: 'asc' });

  const [alert, setAlert] = useState({
    show: false,
    type: 'info', 
    message: '',
    onConfirm: null,
    color: '#059669'
  });

  const [newMp, setNewMp] = useState({
    nom: '', 
    unite_mesure: '', 
    categorie_id: '', 
    cmp: 0, 
    stockAlerte: 10
  });

  const genererIdMp = () => `ART-${Date.now().toString().slice(-6)}`;

  useEffect(() => {
    fetchInitialData();

    const handleGlobalUpdate = (event) => {
        const { table } = event.detail;
        if (['fiches_techniques', 'products', 'psf', 'unites', 'categories_mp', 'all'].includes(table)) {
            fetchInitialData();
        }
    };

    window.addEventListener('ERP_DATA_CHANGED', handleGlobalUpdate);
    return () => window.removeEventListener('ERP_DATA_CHANGED', handleGlobalUpdate);
  }, []);

  // --- SYNCHRONISATION TEMPS RÉEL (SOCKET) ---
  useEffect(() => {
    if (!socket) return;

    const handleUpdate = () => {
        console.log("🔄 Mise à jour du catalogue MP via Socket");
        fetchInitialData(); 
    };

    socket.on('RAW_MATERIAL_CHANGED', handleUpdate);
    socket.on('REFRESH_UI', (data) => {
        if (data?.module === 'RAW_MATERIALS' || data?.module === 'STOCK_MP' || data?.module === 'UNITES' || data?.module === 'CATEGORIES_MP') {
            handleUpdate();
        }
    });

    return () => {
        socket.off('RAW_MATERIAL_CHANGED', handleUpdate);
        socket.off('REFRESH_UI');
    };
  }, []);

  const fetchInitialData = async () => {
    try {
      const [mpRes, uRes, catRes] = await Promise.all([
        API.get('/raw-materials').catch(() => ({ data: [] })),
        API.get('/unites').catch(() => ({ data: [] })),
        API.get('/categories-mp').catch(() => API.get('/raw-materials-categories').catch(() => ({ data: [] })))
      ]);

      setMatieresPremieres(Array.isArray(mpRes.data) ? mpRes.data : []);

      const rawUnites = Array.isArray(uRes.data) ? uRes.data : (uRes.data?.unites || uRes.data?.data || []);
      setUnites(Array.isArray(rawUnites) ? rawUnites.filter(u => u.is_active !== 0) : []);

      const rawCategories = Array.isArray(catRes.data) ? catRes.data : (catRes.data?.categories || catRes.data?.data || []);
      setCategories(Array.isArray(rawCategories) ? rawCategories.filter(c => (c.is_active ?? 1) !== 0) : []);

    } catch (err) { 
        showAlert('error', 'Impossible de charger les données.', '#ef4444');
    }
  };

  const showAlert = (type, message, color = '#059669', onConfirm = null) => {
    setAlert({ show: true, type, message, onConfirm, color });
    if (type !== 'confirm') {
      setTimeout(() => setAlert(prev => ({ ...prev, show: false })), 4000);
    }
  };

  const handleQuickSave = async (e) => {
    e.preventDefault();
    const dataToSend = {
      nom: newMp.nom.toUpperCase(), 
      unite_mesure: newMp.unite_mesure,
      categorie_id: newMp.categorie_id || null, 
      cmp: parseFloat(newMp.cmp) || 0,
      stockAlerte: parseFloat(newMp.stockAlerte) || 0,
      is_active: 1 
    };

    try {
      if (editingId) {
        await API.put(`/raw-materials/${editingId}`, dataToSend);
      } else {
        await API.post('/raw-materials', { ...dataToSend, id: genererIdMp(), stock_actuel: 0, stock_reserve: 0 });
      }
      
      setEditingId(null);
      setNewMp({ nom: '', unite_mesure: '', categorie_id: '', cmp: 0, stockAlerte: 10 });
      fetchInitialData();
      setIsFormOpen(false);
      showAlert('success', 'Opération réussie !', '#059669');
    } catch (err) { 
      const errorMessage = err.response?.data?.error || err.response?.data?.message || "Échec de l'enregistrement.";
      showAlert('error', errorMessage, '#ef4444');
    }
  };

  const toggleArchive = (mp) => {
    const isArchiving = mp.is_active === 1;
    const actionLabel = isArchiving ? 'Archiver' : 'Restaurer';
    const nextStatus = isArchiving ? 0 : 1;

    showAlert(
        'confirm',
        `${actionLabel} "${mp.nom}" ? ${isArchiving ? '(Le stock doit être à 0)' : ''}`,
        isArchiving ? '#e11d48' : '#059669',
        async () => {
            try {
                const res = await API.put(`/raw-materials/${mp.id}`, { 
                    ...mp, 
                    is_active: nextStatus 
                });
                fetchInitialData();
                showAlert('success', res.data.message || "Opération réussie", '#059669');
            } catch (err) {
                const msg = err.response?.data?.error || "Erreur de mise à jour";
                showAlert('error', msg, '#ef4444');
            }
        }
    );
  };

  const prepareEdit = (mp, e) => {
    e.stopPropagation(); 
    setEditingId(mp.id);
    setNewMp({
      nom: mp.nom,
      unite_mesure: mp.unite_mesure,
      categorie_id: mp.categorie_id || '', 
      cmp: mp.cmp,
      stockAlerte: mp.stockAlerte
    });
    setIsFormOpen(true);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const processedData = matieresPremieres
    .filter(mp => (activeTab === 'catalogue' ? mp.is_active === 1 : mp.is_active === 0))
    .filter(mp => {
      const matchedCat = categories.find(c => (c.id === mp.categorie_id) || (c.ID === mp.categorie_id));
      const categoryName = mp.categorie_nom || matchedCat?.nom || matchedCat?.NOM || '';
      const search = searchTerm.toLowerCase();
      return mp.nom.toLowerCase().includes(search) || mp.id.toLowerCase().includes(search) || categoryName.toLowerCase().includes(search);
    })
    .sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === 'categorie') {
        const catA = categories.find(c => (c.id === a.categorie_id) || (c.ID === a.categorie_id));
        const catB = categories.find(c => (c.id === b.categorie_id) || (c.ID === b.categorie_id));
        aVal = a.categorie_nom || catA?.nom || catA?.NOM || '';
        bVal = b.categorie_nom || catB?.nom || catB?.NOM || '';
      } else if (sortConfig.key === 'disponible') {
        aVal = Number(a.stock_actuel) - Number(a.stock_reserve || 0);
        bVal = Number(b.stock_actuel) - Number(b.stock_reserve || 0);
      } else if (sortConfig.key === 'valeur') {
        aVal = (Number(a.stock_actuel) - Number(a.stock_reserve || 0)) * Number(a.cmp || 0);
        bVal = (Number(b.stock_actuel) - Number(b.stock_reserve || 0)) * Number(b.cmp || 0);
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

  // --- Styles ---
  const primaryColor = '#059669';
  const borderColor = '#e2e8f0';

  const s = {
    layout: { display: 'flex', height: '100vh', overflow: 'hidden', background: '#f1f5f9' },
    mainArea: { flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', position: 'relative' },
    topSection: { flexShrink: 0, zIndex: 10, background: 'white', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' },
    scrollableContent: { flex: 1, overflow: 'hidden', padding: '20px 40px', display: 'flex', flexDirection: 'column' },
    alertContainer: {
        position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)',
        zIndex: 9999, display: 'flex', alignItems: 'center', gap: '15px',
        padding: '12px 24px', borderRadius: '12px', background: 'white',
        boxShadow: '0 10px 25px rgba(0,0,0,0.15)', borderLeft: `5px solid ${alert.color}`,
        minWidth: '350px', transition: 'all 0.3s ease'
    },
    formPanel: {
      maxHeight: isFormOpen ? '500px' : '0',
      overflow: 'hidden',
      transition: 'all 0.3s ease-out',
      borderBottom: isFormOpen ? `3px solid ${editingId ? '#3b82f6' : primaryColor}` : '0px solid transparent',
      padding: isFormOpen ? '20px 40px' : '0 40px',
    },
    // Ajout d'une hauteur maximale et d'un scroll pour les listes déroulantes et champs
    input: { padding: '12px', borderRadius: '8px', border: `1px solid ${borderColor}`, fontSize: '14px', width: '100%', background: 'white', maxHeight: '200px', overflowY: 'auto' },
    tab: (active) => ({
      padding: '16px 24px', cursor: 'pointer',
      borderBottom: active ? `3px solid ${primaryColor}` : '3px solid transparent',
      color: active ? primaryColor : '#64748b', fontWeight: active ? '800' : '600',
      display: 'flex', alignItems: 'center', gap: '10px', transition: '0.2s'
    }),
    thSortable: {
      padding: '15px', color: '#475569', fontSize: '11px', fontWeight: '800', cursor: 'pointer', userSelect: 'none', background: '#f8fafc'
    },
    btnAction: {
        padding: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: '0.2s'
    }
  };

  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown size={13} style={{ marginLeft: '5px', opacity: 0.4 }} />;
    return sortConfig.direction === 'asc' ? <ArrowUp size={13} style={{ marginLeft: '5px', color: primaryColor }} /> : <ArrowDown size={13} style={{ marginLeft: '5px', color: primaryColor }} />;
  };

  return (
    <div style={s.layout}>
      <Sidebar />

      {alert.show && (
        <div style={s.alertContainer}>
            <div style={{ color: alert.color }}>
                {alert.type === 'confirm' ? <AlertTriangle size={24}/> : (alert.type === 'error' ? <X size={24}/> : <CheckCircle size={24}/>)}
            </div>
            <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>{alert.message}</p>
            </div>
            {alert.type === 'confirm' ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => setAlert({ ...alert, show: false })} style={{ padding: '5px 10px', border: 'none', background: '#f1f5f9', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>NON</button>
                    <button onClick={() => { alert.onConfirm(); setAlert({...alert, show: false}) }} style={{ padding: '5px 10px', border: 'none', background: alert.color, color: 'white', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>OUI</button>
                </div>
            ) : (
                <X size={18} style={{ cursor: 'pointer', color: '#94a3b8' }} onClick={() => setAlert({ ...alert, show: false })} />
            )}
        </div>
      )}

      <main style={s.mainArea}>
        <div style={s.topSection}>
            <header style={{ padding: '20px 40px', borderBottom: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
                <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '900' }}>
                <span style={{color: primaryColor}}>MATIÈRES</span> PREMIÈRES
                </h1>
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ position: 'relative' }}>
                <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={18} />
                <input 
                    type="text" placeholder="Rechercher..." 
                    style={{ padding: '11px 11px 11px 40px', borderRadius: '10px', border: `1px solid ${borderColor}`, width: '250px', outline: 'none' }} 
                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} 
                />
                </div>
                
                <button 
                onClick={() => {
                    if(isFormOpen) { 
                        setEditingId(null); 
                        setNewMp({nom:'', unite_mesure:'', categorie_id:'', cmp:0, stockAlerte:10}); 
                    }
                    setIsFormOpen(!isFormOpen);
                }} 
                style={{ 
                    background: isFormOpen ? '#fee2e2' : primaryColor, 
                    color: isFormOpen ? '#ef4444' : 'white',
                    padding: '10px 20px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px'
                }}
                >
                {isFormOpen ? <X size={18} /> : <Plus size={18} />}
                {isFormOpen ? 'FERMER' : 'NOUVELLE MP'}
                </button>
            </div>
            </header>

            <div style={s.formPanel}>
              <div style={{ maxWidth: '1200px' }}>
                <h2 style={{ fontSize: '13px', fontWeight: '900', color: editingId ? '#3b82f6' : '#1e293b', marginBottom: '15px', textTransform: 'uppercase' }}>
                  {editingId ? `Modification : ${newMp.nom}` : "Nouvelle matière première"}
                </h2>
                <form onSubmit={handleQuickSave} style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 1fr 1fr auto', gap: '15px', alignItems: 'end' }}>
                <div>
                    <label style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', display: 'block', marginBottom: '5px' }}>DÉSIGNATION</label>
                    <input required style={s.input} placeholder="Ex: FARINE" value={newMp.nom} onChange={e => setNewMp({...newMp, nom: e.target.value})} />
                </div>
                <div>
                    <label style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', display: 'block', marginBottom: '5px' }}>CATÉGORIE MP</label>
                  <select 
                    required
                    style={s.input} 
                    value={newMp.categorie_id} 
                    onChange={e => setNewMp({...newMp, categorie_id: e.target.value})}
                  >
                    <option value="">Sélectionner une catégorie...</option>
                    {categories.map(cat => {
                      const catId = cat.id || cat.ID;
                      const catNom = cat.nom || cat.NOM;
                      return (
                        <option key={catId} value={catId}>
                          {catNom}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div>
                    <label style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', display: 'block', marginBottom: '5px' }}>UNITÉ DE MESURE</label>
                    <select 
                      required 
                      style={s.input} 
                      value={newMp.unite_mesure} 
                      onChange={e => setNewMp({...newMp, unite_mesure: e.target.value})}
                    >
                      <option value="">Choisir une unité...</option>
                      {unites.map(u => {
                        const libelleUnite = u.libelle || u.LIBELLE || u.nom || `Unité ${u.id}`;
                        const codeUnite = u.code || u.CODE ? ` (${u.code || u.CODE})` : '';
                        const valeurOption = u.libelle || u.code || u.id; 
                        return (
                          <option key={u.id || u.ID} value={valeurOption}>
                            {libelleUnite}{codeUnite}
                          </option>
                        );
                      })}
                    </select>
                </div>
                <div>
                    <label style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', display: 'block', marginBottom: '5px' }}>CMP</label>
                    <input type="number" step="0.01" style={s.input} value={newMp.cmp} onChange={e => setNewMp({...newMp, cmp: e.target.value})} />
                </div>
                <div>
                    <label style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', display: 'block', marginBottom: '5px' }}>ALERTE</label>
                    <input type="number" style={s.input} value={newMp.stockAlerte} onChange={e => setNewMp({...newMp, stockAlerte: e.target.value})} />
                </div>
                <button type="submit" style={{ background: editingId ? '#3b82f6' : primaryColor, color: 'white', border: 'none', padding: '13px 20px', borderRadius: '8px', fontWeight: '900', cursor: 'pointer', height: '45px' }}>
                    {editingId ? 'METTRE À JOUR' : 'VALIDER'}
                </button>
                </form>
              </div>
            </div>
        </div>

        <div style={s.scrollableContent}>
          <div style={{ display: 'flex', background: 'white', borderRadius: '12px 12px 0 0', border: `1px solid ${borderColor}`, borderBottom: 'none', width: 'fit-content', flexShrink: 0 }}>
            <div style={s.tab(activeTab === 'catalogue')} onClick={() => setActiveTab('catalogue')}>
              <Package size={18} /> Actives ({matieresPremieres.filter(a => a.is_active === 1).length})
            </div>
            <div style={s.tab(activeTab === 'archives')} onClick={() => setActiveTab('archives')}>
              <Archive size={18} /> Archivées ({matieresPremieres.filter(a => a.is_active === 0).length})
            </div>
          </div>

          <div style={{ background: 'white', borderRadius: '0 12px 12px 12px', border: `1px solid ${borderColor}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, marginBottom: '20px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', flexShrink: 0 }}>
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left', borderBottom: `1px solid ${borderColor}` }}>
                  <th style={{ width: '50px', padding: '15px' }}></th>
                  <th style={{ ...s.thSortable, width: '120px' }} onClick={() => handleSort('id')}>
                    IDENTIFIANT {renderSortIcon('id')}
                  </th>
                  <th style={s.thSortable} onClick={() => handleSort('nom')}>
                    DÉSIGNATION {renderSortIcon('nom')}
                  </th>
                  <th style={{ ...s.thSortable, width: '150px' }} onClick={() => handleSort('categorie')}>
                    CATÉGORIE {renderSortIcon('categorie')}
                  </th>
                  <th style={{ ...s.thSortable, width: '130px' }} onClick={() => handleSort('stock_actuel')}>
                    STOCK PHYSIQUE {renderSortIcon('stock_actuel')}
                  </th>
                  <th style={{ ...s.thSortable, width: '100px' }} onClick={() => handleSort('stock_reserve')}>
                    RÉSERVÉ {renderSortIcon('stock_reserve')}
                  </th>
                  <th style={{ ...s.thSortable, width: '120px' }} onClick={() => handleSort('disponible')}>
                    DISPONIBLE {renderSortIcon('disponible')}
                  </th>
                  <th style={{ ...s.thSortable, width: '100px' }} onClick={() => handleSort('cmp')}>
                    CMP {renderSortIcon('cmp')}
                  </th>
                  <th style={{ ...s.thSortable, width: '130px' }} onClick={() => handleSort('valeur')}>
                    VALEUR (DISPO) {renderSortIcon('valeur')}
                  </th>
                  <th style={{ padding: '15px', textAlign: 'right', color: '#475569', fontSize: '11px', fontWeight: '800', background: '#f8fafc', width: '110px' }}>ACTIONS</th>
                </tr>
              </thead>
            </table>

            <div style={{ overflowY: 'auto', flex: 1, width: '100%' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <tbody>
                  {processedData.map(mp => {
                    const stockDisponible = Number(mp.stock_actuel) - Number(mp.stock_reserve || 0);
                    
                    const matchedCat = categories.find(c => (c.id === mp.categorie_id) || (c.ID === mp.categorie_id));
                    const categoryName = mp.categorie_nom || matchedCat?.nom || matchedCat?.NOM || 'Non catégorisé';

                    return (
                      <tr key={mp.id} style={{ borderBottom: `1px solid ${borderColor}`, background: expandedId === mp.id ? '#f0fdf4' : 'transparent' }} onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = expandedId === mp.id ? '#f0fdf4' : 'transparent'}>
                        <td style={{ width: '50px', padding: '15px', textAlign: 'center', cursor: 'pointer' }} onClick={() => setExpandedId(expandedId === mp.id ? null : mp.id)}>
                          {expandedId === mp.id ? <ChevronDown size={18} color={primaryColor} /> : <ChevronRight size={18} color="#cbd5e1" />}
                        </td>
                        <td style={{ width: '120px', padding: '15px', fontSize: '12px', color: primaryColor, fontWeight: '700', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {mp.id}
                        </td>
                        <td style={{ padding: '15px', fontWeight: '800', color: '#0f172a', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {mp.nom}
                        </td>
                        <td style={{ width: '150px', padding: '15px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                           <span style={{ fontSize: '11px', color: '#475569', background: '#f1f5f9', padding: '4px 8px', borderRadius: '6px', fontWeight: '700', border: `1px solid ${borderColor}` }}>
                             📁 {categoryName}
                           </span>
                        </td>
                        <td style={{ width: '130px', padding: '15px', whiteSpace: 'nowrap' }}>
                          <div style={{ fontWeight: '700', color: '#64748b' }}>
                            {Number(mp.stock_actuel).toFixed(3)} <span style={{fontSize: '10px'}}>{mp.unite_mesure}</span>
                          </div>
                        </td>
                        <td style={{ width: '100px', padding: '15px', whiteSpace: 'nowrap' }}>
                          <div style={{ fontWeight: '700', color: '#f59e0b' }}>
                            {Number(mp.stock_reserve || 0).toFixed(3)}
                          </div>
                        </td>
                        <td style={{ width: '120px', padding: '15px', whiteSpace: 'nowrap' }}>
                          <div style={{ fontWeight: '900', color: stockDisponible <= mp.stockAlerte ? '#ef4444' : '#10b981' }}>
                            {stockDisponible.toFixed(3)} <span style={{fontSize: '10px'}}>{mp.unite_mesure}</span>
                          </div>
                        </td>
                        <td style={{ width: '100px', padding: '15px', fontWeight: '800', whiteSpace: 'nowrap' }}>
                          {Number(mp.cmp).toLocaleString()}
                        </td>
                        <td style={{ width: '130px', padding: '15px', fontWeight: '800', whiteSpace: 'nowrap' }}>
                          {(stockDisponible * Number(mp.cmp || 0)).toLocaleString()} 
                        </td>
                        <td style={{ width: '110px', padding: '15px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            {mp.is_active === 1 ? (
                              <>
                                <button onClick={(e) => prepareEdit(mp, e)} style={{ ...s.btnAction, background: '#eff6ff', color: '#3b82f6' }} title="Modifier">
                                  <Edit2 size={16} />
                                </button>
                                <button 
                                  onClick={() => toggleArchive(mp)} 
                                  style={{ 
                                      ...s.btnAction, 
                                      background: mp.stock_actuel > 0 ? '#f1f5f9' : '#fff1f2', 
                                      color: mp.stock_actuel > 0 ? '#94a3b8' : '#e11d48',
                                      cursor: mp.stock_actuel > 0 ? 'not-allowed' : 'pointer'
                                  }}
                                  title="Archiver"
                                >
                                  <Archive size={16} />
                                </button>
                              </>
                            ) : (
                              <button onClick={() => toggleArchive(mp)} style={{ ...s.btnAction, background: '#f0fdf4', color: '#16a34a', gap: '5px', fontSize: '12px', fontWeight: 'bold', padding: '8px 15px' }}>
                                <CheckCircle size={16} /> RESTAURER
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ListeMp;